import React, { useCallback, useEffect, useRef, useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { ISelectedProtocol } from "../../../../services/types/utils.types";
import {
  PROTOCOL_FREQUENCY_AFTER_EACH,
  PROTOCOL_FREQUENCY_OPTIONS,
  PROTOCOL_FREQUENCY_SEQUENTIAL,
  TProtocolFrequencyType,
  protocolFrequencyTakesEveryN,
} from "../../../../services/types/treatmentProtocol.types";
import styles from "./CreateAppointmentModal.module.css";

interface IProtocolOption {
  id: number;
  desc: string;
}

interface IProtocolsManagerProps {
  repeatCount: number;
  protocols: IProtocolOption[];
  selectedProtocols: ISelectedProtocol[];
  onChange: (protocols: ISelectedProtocol[]) => void;
  onValidationChange: (isValid: boolean) => void;
}

function validateProtocolRow(
  protocol: ISelectedProtocol,
  repeatCount: number
): { isValid: boolean; errorMessage: string } {
  // Every other cadence is expressible in a plan of any length: a single-session plan still has a
  // first, a middle and a last session, and they are all the same session.
  if (!protocolFrequencyTakesEveryN(protocol.frequencyType)) {
    return { isValid: true, errorMessage: "" };
  }

  const val = protocol.everyNSessions;
  if (!val || val < 1 || val > repeatCount) {
    return {
      isValid: false,
      errorMessage: t("PROTOCOL_SEQ_VALIDATION_ERROR", { count: repeatCount }),
    };
  }

  return { isValid: true, errorMessage: "" };
}

function ProtocolsManager({
  repeatCount,
  protocols,
  selectedProtocols,
  onChange,
  onValidationChange,
}: IProtocolsManagerProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const validateAll = useCallback(() => {
    if (selectedProtocols.length === 0) {
      onValidationChange(true);
      return;
    }

    const allValid = selectedProtocols.every(
      (p) => validateProtocolRow(p, repeatCount).isValid
    );
    onValidationChange(allValid);
  }, [selectedProtocols, repeatCount, onValidationChange]);

  useEffect(() => {
    validateAll();
  }, [validateAll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleProtocol = useCallback(
    (protocol: IProtocolOption, checked: boolean) => {
      if (checked) {
        onChange([
          ...selectedProtocols,
          {
            protocolId: protocol.id,
            name: protocol.desc,
            frequencyType: PROTOCOL_FREQUENCY_AFTER_EACH,
            everyNSessions: Math.min(3, repeatCount),
          },
        ]);
      } else {
        onChange(selectedProtocols.filter((p) => p.protocolId !== protocol.id));
      }
    },
    [selectedProtocols, onChange, repeatCount]
  );

  const handleRemove = useCallback(
    (protocolId: number) => {
      onChange(selectedProtocols.filter((p) => p.protocolId !== protocolId));
    },
    [selectedProtocols, onChange]
  );

  const handleFrequencyChange = useCallback(
    (protocolId: number, frequencyType: TProtocolFrequencyType) => {
      onChange(
        selectedProtocols.map((p) =>
          p.protocolId === protocolId ? { ...p, frequencyType } : p
        )
      );
    },
    [selectedProtocols, onChange]
  );

  const handleEveryNChange = useCallback(
    (protocolId: number, value: number) => {
      onChange(
        selectedProtocols.map((p) =>
          p.protocolId === protocolId ? { ...p, everyNSessions: value } : p
        )
      );
    },
    [selectedProtocols, onChange]
  );

  const selectedIds = new Set(selectedProtocols.map((p) => p.protocolId));

  return (
    <div className={styles.planCard}>
      <div className={styles.planCardHeader}>
        <span>{t("PROTOCOLS") || "פרוטוקולים"}</span>
      </div>

      <div className={styles.protocolSelectWrap} ref={wrapRef}>
        <button
          type="button"
          className={styles.protocolAddBtn}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        >
          + {t("ADD_PROTOCOL") || "הוספת פרוטוקול"}
        </button>
        <div className={`${styles.protocolMenu} ${isMenuOpen ? styles.protocolMenuOpen : ""}`}>
          {protocols.length === 0 ? (
            <div className={styles.protocolMenuItem}>{t("NO_RESULTS")}</div>
          ) : (
            protocols.map((protocol) => (
              <label key={protocol.id} className={styles.protocolMenuItem}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(protocol.id)}
                  onChange={(e) => handleToggleProtocol(protocol, e.target.checked)}
                />
                {protocol.desc}
              </label>
            ))
          )}
        </div>
      </div>

      <div className={styles.protocolList}>
        {selectedProtocols.length === 0 ? (
          <div className={styles.emptyProtocols}>
            {t("NO_PROTOCOLS_SELECTED") || "טרם נבחרו פרוטוקולים"}
          </div>
        ) : (
          selectedProtocols.map((protocol) => {
            const rowId = `protocol-${protocol.protocolId}`;
            const { isValid, errorMessage } = validateProtocolRow(protocol, repeatCount);
            const isSequential = protocolFrequencyTakesEveryN(protocol.frequencyType);
            const showError = !isValid && errorMessage;

            return (
              <div key={protocol.protocolId} className={styles.protocolItem}>
                <span className={styles.protocolName}>{protocol.name}</span>
                <div className={styles.freqOptions}>
                  {PROTOCOL_FREQUENCY_OPTIONS.map((option) => (
                    <React.Fragment key={option.value}>
                      <label className={styles.freqOption}>
                        <input
                          type="radio"
                          name={rowId}
                          value={option.value}
                          checked={protocol.frequencyType === option.value}
                          onChange={() =>
                            handleFrequencyChange(protocol.protocolId, option.value)
                          }
                        />
                        {t(option.labelKey)}
                      </label>

                      {/* The count belongs to the sequential option, so it sits with it. */}
                      {option.value === PROTOCOL_FREQUENCY_SEQUENTIAL && (
                        <div
                          className={`${styles.seqInput} ${
                            !isSequential ? styles.seqInputDisabled : ""
                          }`}
                        >
                          {t("PROTOCOL_ONCE_EVERY") || "אחת ל-"}
                          <input
                            type="number"
                            min={1}
                            max={repeatCount}
                            className={`${styles.seqNumberInput} ${
                              isSequential && !isValid ? styles.seqNumberInputInvalid : ""
                            }`}
                            value={protocol.everyNSessions}
                            disabled={!isSequential}
                            onChange={(e) =>
                              handleEveryNChange(
                                protocol.protocolId,
                                parseInt(e.target.value, 10) || 0
                              )
                            }
                          />
                          {t("PROTOCOL_SESSIONS") || "מפגשים"}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.protocolRemove}
                  onClick={() => handleRemove(protocol.protocolId)}
                  aria-label={t("REMOVE") || "הסר"}
                >
                  ✕
                </button>
                {showError && (
                  <div className={`${styles.seqError} ${styles.seqErrorShow}`}>
                    {errorMessage}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ProtocolsManager;
